import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import taskService from '../../services/taskService';

const TaskStats = ({ userId = null, refreshTrigger = 0 }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStats();
  }, [userId, refreshTrigger]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = userId ? { assigned_to: userId } : {};
      const response = await taskService.getTaskStats(params);
      setStats(response);
    } catch (err) {
      setError(err.message);
      console.error('Error loading task stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-card border border-border rounded-lg p-6 animate-pulse">
            <div className="h-8 bg-muted rounded mb-2"></div>
            <div className="h-6 bg-muted rounded mb-1"></div>
            <div className="h-4 bg-muted rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <Icon name="AlertCircle" size={20} className="text-red-600 mr-3" />
          <div>
            <h3 className="text-red-800 font-medium">Error Loading Statistics</h3>
            <p className="text-red-700 text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const getCompletionPercentage = () => {
    if (stats.total_tasks === 0) return 0;
    return Math.round(stats.completion_rate * 100);
  };

  const getOverduePercentage = () => {
    if (stats.total_tasks === 0) return 0;
    return Math.round((stats.overdue_tasks / stats.total_tasks) * 100);
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const statCards = [
    {
      title: 'Total Tasks',
      value: stats.total_tasks,
      icon: 'ListTodo',
      color: 'blue',
      description: 'All tasks assigned',
      trend: null
    },
    {
      title: 'Pending',
      value: stats.pending_tasks,
      icon: 'Clock',
      color: 'yellow',
      description: 'Awaiting action',
      percentage: stats.total_tasks > 0 ? Math.round((stats.pending_tasks / stats.total_tasks) * 100) : 0
    },
    {
      title: 'In Progress',
      value: stats.in_progress_tasks,
      icon: 'Play',
      color: 'blue',
      description: 'Currently active',
      percentage: stats.total_tasks > 0 ? Math.round((stats.in_progress_tasks / stats.total_tasks) * 100) : 0
    },
    {
      title: 'Completed',
      value: stats.completed_tasks,
      icon: 'CheckCircle',
      color: 'green',
      description: 'Successfully finished',
      percentage: getCompletionPercentage()
    },
    {
      title: 'Overdue',
      value: stats.overdue_tasks,
      icon: 'AlertTriangle',
      color: 'red',
      description: 'Past due date',
      percentage: getOverduePercentage(),
      alert: stats.overdue_tasks > 0
    },
    {
      title: 'High Priority',
      value: stats.high_priority_tasks,
      icon: 'ArrowUp',
      color: 'orange',
      description: 'High priority tasks',
      percentage: stats.total_tasks > 0 ? Math.round((stats.high_priority_tasks / stats.total_tasks) * 100) : 0
    },
    {
      title: 'Urgent',
      value: stats.urgent_priority_tasks,
      icon: 'Zap',
      color: 'red',
      description: 'Urgent tasks',
      percentage: stats.total_tasks > 0 ? Math.round((stats.urgent_priority_tasks / stats.total_tasks) * 100) : 0,
      alert: stats.urgent_priority_tasks > 0
    },
    {
      title: 'Avg. Completion',
      value: formatDuration(stats.avg_completion_time),
      icon: 'Timer',
      color: 'purple',
      description: 'Average completion time',
      trend: null
    }
  ];

  const getCardColorClasses = (color, alert = false) => {
    if (alert) {
      return {
        bg: 'bg-red-50',
        border: 'border-red-200',
        icon: 'text-red-600',
        text: 'text-red-900',
        value: 'text-red-700'
      };
    }

    const colors = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        icon: 'text-blue-600',
        text: 'text-blue-900',
        value: 'text-blue-700'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        icon: 'text-green-600',
        text: 'text-green-900',
        value: 'text-green-700'
      },
      yellow: {
        bg: 'bg-yellow-50',
        border: 'border-yellow-200',
        icon: 'text-yellow-600',
        text: 'text-yellow-900',
        value: 'text-yellow-700'
      },
      orange: {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        icon: 'text-orange-600',
        text: 'text-orange-900',
        value: 'text-orange-700'
      },
      red: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        icon: 'text-red-600',
        text: 'text-red-900',
        value: 'text-red-700'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        icon: 'text-purple-600',
        text: 'text-purple-900',
        value: 'text-purple-700'
      }
    };

    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const colorClasses = getCardColorClasses(stat.color, stat.alert);
          
          return (
            <div
              key={index}
              className={`${colorClasses.bg} ${colorClasses.border} border rounded-lg p-6 transition-all hover:shadow-md`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`${colorClasses.icon} p-2 rounded-lg bg-white/50`}>
                    <Icon name={stat.icon} size={24} />
                  </div>
                  <div>
                    <h3 className={`text-sm font-medium ${colorClasses.text}`}>
                      {stat.title}
                    </h3>
                    <p className={`text-2xl font-bold ${colorClasses.value}`}>
                      {stat.value}
                    </p>
                  </div>
                </div>
                {stat.percentage !== undefined && (
                  <div className={`text-right ${colorClasses.text}`}>
                    <p className="text-sm font-medium">
                      {stat.percentage}%
                    </p>
                  </div>
                )}
              </div>
              <p className={`text-xs ${colorClasses.text} mt-2 opacity-75`}>
                {stat.description}
              </p>
            </div>
          );
        })}
      </div>

      {/* Detailed Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Task Types Breakdown */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Tasks by Type
          </h3>
          <div className="space-y-3">
            {Object.entries(stats.by_type || {}).map(([type, count]) => {
              const typeConfig = taskService.getTaskTypes().find(t => t.value === type);
              const percentage = stats.total_tasks > 0 ? Math.round((count / stats.total_tasks) * 100) : 0;
              
              return (
                <div key={type} className="flex items-center justify-between">
                  <span className="text-sm text-foreground">
                    {typeConfig?.label || type}
                  </span>
                  <div className="flex items-center space-x-2">
                    <div className="w-20 bg-muted rounded-full h-2">
                      <div
                        className="bg-primary rounded-full h-2"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-sm text-muted-foreground w-8 text-right">
                      {count}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Task Categories Breakdown */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Tasks by Category
          </h3>
          <div className="space-y-3">
            {Object.entries(stats.by_category || {}).map(([category, count]) => {
              const categoryConfig = taskService.getTaskCategories().find(c => c.value === category);
              const percentage = stats.total_tasks > 0 ? Math.round((count / stats.total_tasks) * 100) : 0;
              
              return (
                <div key={category} className="flex items-center justify-between">
                  <span className="text-sm text-foreground">
                    {categoryConfig?.label || category}
                  </span>
                  <div className="flex items-center space-x-2">
                    <div className="w-20 bg-muted rounded-full h-2">
                      <div
                        className="bg-secondary rounded-full h-2"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-sm text-muted-foreground w-8 text-right">
                      {count}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Completion Rate Summary */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">
            Task Completion Summary
          </h3>
          <div className="text-right">
            <p className="text-2xl font-bold text-foreground">
              {getCompletionPercentage()}%
            </p>
            <p className="text-sm text-muted-foreground">
              Completion Rate
            </p>
          </div>
        </div>
        
        <div className="w-full bg-muted rounded-full h-3 mb-4">
          <div
            className="bg-green-500 rounded-full h-3 transition-all duration-500"
            style={{ width: `${getCompletionPercentage()}%` }}
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-sm text-muted-foreground">Pending</p>
            <p className="text-lg font-semibold text-yellow-600">{stats.pending_tasks}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">In Progress</p>
            <p className="text-lg font-semibold text-blue-600">{stats.in_progress_tasks}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Completed</p>
            <p className="text-lg font-semibold text-green-600">{stats.completed_tasks}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Overdue</p>
            <p className="text-lg font-semibold text-red-600">{stats.overdue_tasks}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskStats;